import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, Users, Mail, Briefcase, MapPinned, Newspaper, HelpCircle, Files, Image, Settings, KeyRound, LogOut, Menu, X, ExternalLink, Loader2, PhoneCall, FolderLock, BarChart3, FileDown, BellRing, ClipboardList, Music } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/site/Seo";

const NAV = [
  ["/admin", "Genel Bakış", LayoutDashboard, true],
  ["/admin/basvurular", "İş Başvuruları", Users],
  ["/admin/gorevler", "Görev ve İlanlar", ClipboardList],
  ["/admin/geri-arama", "Telefon ve Geri Arama", PhoneCall],
  ["/admin/aydinfest", "Aydınfest Başvuruları", Music],
  ["/admin/belgeler", "Başvuru Belgeleri", FolderLock],
  ["/admin/raporlar", "Başvuru Raporları", BarChart3],
  ["/admin/disa-aktarma", "Excel ve PDF Dışa Aktarma", FileDown],
  ["/admin/bildirimler", "E-posta Bildirim Ayarları", BellRing],
  ["/admin/hesap", "Yönetici Hesabı ve Güvenlik", KeyRound],
  ["/admin/ayarlar", "Sistem Ayarları", Settings],
];
const SITE_NAV = [
  ["/admin/teklifler", "Teklif Talepleri", FileText],
  ["/admin/mesajlar", "İletişim Mesajları", Mail],
  ["/admin/hizmetler", "Hizmetler", Briefcase],
  ["/admin/sektorler", "Sektör / Bölge", MapPinned],
  ["/admin/blog", "Blog", Newspaper],
  ["/admin/sss", "SSS", HelpCircle],
  ["/admin/sayfalar", "Sayfalar ve Metinler", Files],
  ["/admin/medya", "Medya", Image],
];

function NavItems({ items }) {
  return items.map(([to, label, Icon, end]) => (
    <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[color:var(--red)] text-white" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-nav-${to.split("/")[2] || "panel"}`}>
      <Icon className="h-4 w-4 shrink-0" /> {label}
    </NavLink>
  ));
}

export function Spinner() {
  return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--red)]" /></div>;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/admin/giris" replace />;

  const side = (
    <nav className="flex flex-col gap-0.5 p-3">
      <NavItems items={NAV} />
      <p className="mt-4 px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Web sitesi</p>
      <NavItems items={SITE_NAV} />
    </nav>
  );
  return (
    <div className="min-h-screen bg-zinc-100">
      <Seo title="Yönetim Paneli | Rüzgar Özel Güvenlik" noindex path="/admin" />
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto bg-[color:var(--ink)] transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center gap-3 border-b border-white/10 px-5">
          <img src="/assets/logo-shield.webp" alt="" className="h-9" />
          <span className="font-display text-sm font-extrabold text-white">Yönetim Paneli</span>
        </div>
        {side}
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-zinc-200 bg-white px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menü" data-testid="admin-menu-toggle">{open ? <X /> : <Menu />}</button>
          <span className="hidden text-sm text-zinc-500 sm:block">Hoş geldiniz, <b className="text-zinc-900">{user.name}</b></span>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /> Siteyi gör</Link>
            <button onClick={logout} className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Çıkış</button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}

export function AdminTitle({ title, desc, children }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold text-zinc-950" data-testid="admin-page-title">{title}</h1>
        {desc && <p className="mt-1 text-sm text-zinc-500">{desc}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}
